// frontend/src/pages/Communities/Communities.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import './Communities.css';

const Communities = () => {
  const [communities, setCommunities] = useState([]);
  const [joinedCommunities, setJoinedCommunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '',
    description: '',
    grade: '',
    topics: [],
    privacy: 'public'
  });
  const { user } = useAuth();

  useEffect(() => {
    fetchCommunities();
  }, [user]);

  // Helper function to safely extract array from response
  const extractArray = (response) => {
    if (!response) return [];
    
    const data = response.data;
    
    // If data is already an array, return it
    if (Array.isArray(data)) return data;
    
    // If data is an object, check common property names
    if (data && typeof data === 'object') {
      if (Array.isArray(data.communities)) return data.communities;
      if (Array.isArray(data.data)) return data.data;
      if (Array.isArray(data.results)) return data.results;
      if (Array.isArray(data.items)) return data.items;
    }
    
    // Return empty array as fallback
    return [];
  };

  const fetchCommunities = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Fetch all communities
      const allResponse = await axios.get('/api/communities');
      const allCommunities = extractArray(allResponse);
      setCommunities(allCommunities);
      
      // Fetch joined communities if user is logged in
      if (user && token) {
        try {
          const joinedResponse = await axios.get(
            '/api/communities/joined',
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const joined = extractArray(joinedResponse);
          setJoinedCommunities(joined);
        } catch (joinedError) {
          console.error('Error fetching joined communities:', joinedError);
          // Fallback: filter from all communities based on user membership
          if (user._id || user.id) {
            const userId = user._id || user.id;
            const joined = allCommunities.filter(c => 
              c.members?.includes(userId) || 
              c.members?.some(m => m._id === userId || m === userId)
            );
            setJoinedCommunities(joined);
          } else {
            setJoinedCommunities([]);
          }
        }
      } else {
        setJoinedCommunities([]);
      }
    } catch (error) {
      console.error('Error fetching communities:', error);
      setCommunities([]);
      setJoinedCommunities([]);
    } finally {
      setLoading(false);
    }
  };

  const handleJoinCommunity = async (communityId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `/api/communities/${communityId}/join`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchCommunities();
    } catch (error) {
      console.error('Error joining community:', error);
      alert(error.response?.data?.error || error.response?.data?.message || 'Error joining community');
    }
  };

  const handleLeaveCommunity = async (communityId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `/api/communities/${communityId}/leave`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchCommunities();
    } catch (error) {
      console.error('Error leaving community:', error);
      alert(error.response?.data?.error || error.response?.data?.message || 'Error leaving community');
    }
  };

  const handleCreateCommunity = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        '/api/communities/create',
        createForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowCreateModal(false);
      setCreateForm({
        name: '',
        description: '',
        grade: '',
        topics: [],
        privacy: 'public'
      });
      fetchCommunities();
    } catch (error) {
      console.error('Error creating community:', error);
      alert(error.response?.data?.error || error.response?.data?.message || 'Error creating community');
    }
  };

  const isJoined = (communityId) => {
    if (!Array.isArray(joinedCommunities)) return false;
    return joinedCommunities.some(c => c._id === communityId || c.id === communityId);
  };

  // Safely get the list to display
  const getDisplayList = () => {
    const list = activeTab === 'all' ? communities : joinedCommunities;
    // Ensure we have an array
    if (!Array.isArray(list)) return [];
    return list;
  };

  const filteredCommunities = getDisplayList().filter(c => {
    if (!c) return false;
    const name = c.name || '';
    const description = c.description || '';
    const query = searchQuery.toLowerCase();
    return name.toLowerCase().includes(query) || description.toLowerCase().includes(query);
  });

  const availableTopics = [
    'Algebra', 'Functions', 'Finance', 'Trigonometry', 
    'Euclidean Geometry', 'Analytical Geometry', 'Statistics', 
    'Probability', 'Calculus', 'Patterns and Sequences', 'Number Patterns'
  ];

  if (loading) {
    return (
      <div className="communities-loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="communities-container">
      <div className="communities-header">
        <h1>Communities</h1>
        {user && (
          <button 
            onClick={() => setShowCreateModal(true)}
            className="create-community-btn"
          >
            Create Community
          </button>
        )}
      </div>

      <div className="communities-search">
        <input
          type="text"
          placeholder="Search communities..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="search-input"
        />
      </div>

      <div className="communities-tabs">
        <button 
          className={`tab ${activeTab === 'all' ? 'active' : ''}`}
          onClick={() => setActiveTab('all')}
        >
          All Communities
        </button>
        {user && (
          <button 
            className={`tab ${activeTab === 'joined' ? 'active' : ''}`}
            onClick={() => setActiveTab('joined')}
          >
            Joined Communities ({Array.isArray(joinedCommunities) ? joinedCommunities.length : 0})
          </button>
        )}
      </div>

      <div className="communities-list">
        {filteredCommunities.length === 0 ? (
          <div className="no-communities">
            <p>
              {searchQuery 
                ? 'No communities found matching your search.'
                : activeTab === 'all' 
                  ? 'No communities found. Be the first to create one!'
                  : "You haven't joined any communities yet."}
            </p>
          </div>
        ) : (
          filteredCommunities.map(community => (
            <div key={community._id || community.id} className="community-card">
              <div className="community-info">
                <h3>{community.name}</h3>
                <p className="community-description">{community.description}</p>
                <div className="community-meta">
                  <span className="member-count">
                    {community.members?.length || 0} members
                  </span>
                  {community.grade && (
                    <span className="grade-badge">Grade {community.grade}</span>
                  )}
                  {community.topics?.map(topic => (
                    <span key={topic} className="topic-badge">{topic}</span>
                  ))}
                  <span className="privacy-badge">{community.privacy || 'public'}</span>
                </div>
              </div>
              <div className="community-actions">
                <Link 
                  to={`/communities/${community._id || community.id}`}
                  className="view-community-btn"
                >
                  View
                </Link>
                {user && (
                  isJoined(community._id || community.id) ? (
                    <button 
                      className="leave-btn"
                      onClick={() => handleLeaveCommunity(community._id || community.id)}
                    >
                      Leave
                    </button>
                  ) : (
                    <button 
                      className="join-btn"
                      onClick={() => handleJoinCommunity(community._id || community.id)}
                    >
                      Join
                    </button>
                  )
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create Community Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create New Community</h2>
              <button 
                className="modal-close"
                onClick={() => setShowCreateModal(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleCreateCommunity}>
              <div className="form-group">
                <label>Community Name</label>
                <input
                  type="text"
                  value={createForm.name}
                  onChange={(e) => setCreateForm({...createForm, name: e.target.value})}
                  required
                  placeholder="Enter community name"
                />
              </div>
              
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={createForm.description}
                  onChange={(e) => setCreateForm({...createForm, description: e.target.value})}
                  required
                  placeholder="Describe your community"
                  rows="3"
                />
              </div>
              
              <div className="form-group">
                <label>Grade (Optional)</label>
                <select
                  value={createForm.grade}
                  onChange={(e) => setCreateForm({...createForm, grade: e.target.value})}
                >
                  <option value="">All Grades</option>
                  <option value="10">Grade 10</option>
                  <option value="11">Grade 11</option>
                  <option value="12">Grade 12</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>Topics</label>
                <div className="topics-select">
                  {availableTopics.map(topic => (
                    <label key={topic} className="topic-checkbox">
                      <input
                        type="checkbox"
                        checked={createForm.topics.includes(topic)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setCreateForm({
                              ...createForm, 
                              topics: [...createForm.topics, topic]
                            });
                          } else {
                            setCreateForm({
                              ...createForm,
                              topics: createForm.topics.filter(t => t !== topic)
                            });
                          }
                        }}
                      />
                      <span>{topic}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              <div className="form-group">
                <label>Privacy</label>
                <select
                  value={createForm.privacy}
                  onChange={(e) => setCreateForm({...createForm, privacy: e.target.value})}
                >
                  <option value="public">Public</option>
                  <option value="private">Private</option>
                </select>
              </div>
              
              <div className="modal-actions">
                <button 
                  type="button" 
                  className="btn-cancel"
                  onClick={() => setShowCreateModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-create">
                  Create Community
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Communities;